import { useState, useEffect, useCallback, useMemo } from 'react';
import { useToast, useDisclosure } from '@chakra-ui/react';
import { quickbooksService } from '../../../api/quickbooks';
import { GL_SYNC_DATES, TB_SYNC_DATES } from '../../../config/syncDefaults';
import { useNavigate } from 'react-router-dom';
import { updateCustomer } from '../../../api/customers';

export const useAccountManagementData = () => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nextCursor, setNextCursor] = useState(null);
  const [previousCursor, setPreviousCursor] = useState(null);
  const [syncingCustomerId, setSyncingCustomerId] = useState(null);
  const [fullSyncingCustomerId, setFullSyncingCustomerId] = useState(null);
  const [activeJobs, setActiveJobs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const toast = useToast();
  const navigate = useNavigate();

  const { isOpen: isSyncModalOpen, onOpen: openSyncModal, onClose: closeSyncModal } = useDisclosure();
  const { isOpen: isDisconnectModalOpen, onOpen: openDisconnectModal, onClose: closeDisconnectModal } = useDisclosure();
  const [selectedCustomerForSync, setSelectedCustomerForSync] = useState(null);
  const [selectedCustomerForDisconnect, setSelectedCustomerForDisconnect] = useState(null);
  const [syncType, setSyncType] = useState('all');
  const [fetchMode, setFetchMode] = useState('cdc');
  const [importMode, setImportMode] = useState('import');
  const [syncDates, setSyncDates] = useState({
    startDate: '',
    endDate: ''
  });
  const [glDates, setGlDates] = useState(GL_SYNC_DATES);
  const [tbDates, setTbDates] = useState(TB_SYNC_DATES);

  // Memoized map of all customer IDs to their index in the customers array
  const customersMap = useMemo(() => {
    const map = {};
    customers.forEach((customer, idx) => {
      map[customer.id] = idx;
    });
    return map;
  }, [customers]);

  // Applies the last_sync_before filter if requested
  const _applyLastSyncBeforeFilter = useCallback(({ customers, lastSyncBefore }) => {
    if (!lastSyncBefore) return customers;
    const filterDate = new Date(lastSyncBefore);
    return customers.filter(customer => {
      if (!customer.quickbooks_last_sync) return true;
      const lastSyncDate = new Date(customer.quickbooks_last_sync);
      return lastSyncDate < filterDate;
    });
  }, []);
  
  const processAPIResponses = useCallback((custResponse, jobsResponse, params) => {
    let customersData = custResponse.data.results || custResponse.data || [];
    // Only apply last_sync_before filter client-side
    customersData = _applyLastSyncBeforeFilter({
      customers: customersData,
      lastSyncBefore: params.last_sync_before
    });
    setCustomers(customersData);
    setFilteredCustomers(customersData);
    setNextCursor(custResponse.data.next);
    setPreviousCursor(custResponse.data.previous);
    setActiveJobs(jobsResponse.data.results || []);
  }, [_applyLastSyncBeforeFilter]);

  const fetchData = useCallback(async (cursor = null, search = '', additionalParams = {}) => {
    setLoading(true);
    try {
      let params = { ...additionalParams };
      // Always request sync_statuses
      params.include_quickbooks_sync_info = true;
      // Include notes in customer fetches
      params.include_notes = true;
      // Include QuickBooks connection status in customer fetches
      params.include_quickbooks_connection = true;
      
      // For cursor-based pagination, we should not modify the cursor parameter
      // as it contains encoded information about the page position
      if (cursor) {
        console.log('Using pagination cursor:', cursor);
        
        // If cursor already has the complete URL with params, we shouldn't extract just the cursor param
        // Instead, use the cursor directly in API calls that accept a full URL
        if (!params.cursor && Object.keys(params).length === 0) {
          // When using direct cursor URLs without extracted params, 
          // don't add any additional params to avoid duplication
          console.log('Using direct cursor without param extraction');
          
          // Make a single API call with the cursor URL - this preserves filters from the cursor
          const [custResponse, jobsResponse] = await Promise.all([
            quickbooksService.getCustomers(cursor),
            quickbooksService.getActiveSyncJobs(),
          ]);
          processAPIResponses(custResponse, jobsResponse, params);
          return;
        }
      }
      if (search) {
        params.search = search;
      }
      
      // Get the connection status from additionalParams
      const connectionStatus = additionalParams.quickbooksConnectionStatus || 'all';
      
      // Make a single API call that respects the filtering parameters
      const [custResponse, jobsResponse] = await Promise.all([
        quickbooksService.getCustomers(1, params, { 
          quickbooksConnectionStatus: connectionStatus 
        }),
        quickbooksService.getActiveSyncJobs(),
      ]);
      processAPIResponses(custResponse, jobsResponse, params);
    } catch (error) {
      console.error('Error fetching account management data:', error);
      toast({ 
        title: 'Error', 
        description: 'Failed to fetch account data.', 
        status: 'error',
        duration: 10000,
        isClosable: true
      });
    }
    setLoading(false);
  }, [toast, processAPIResponses]);

  const handleNextPage = useCallback(() => {
    if (nextCursor) {
      console.log('Navigating to next page with cursor:', nextCursor);
      
      /* Extract non-cursor params to maintain filters when paginating */
      let params = {};
      try {
        const urlObj = new URL(nextCursor);
        urlObj.searchParams.forEach((value, key) => {
          params[key] = value;
        });
        
        console.log('Extracted params for next page:', params);
        // Pass empty string as search term to avoid overriding search params in the cursor
        fetchData(nextCursor, '', params);
      } catch (error) {
        console.error('Error parsing nextCursor URL:', error);
        // Fallback to simpler approach if URL parsing fails
        fetchData(nextCursor, searchTerm);
      }
    }
  }, [nextCursor, fetchData, searchTerm]);

  const handlePreviousPage = useCallback(() => {
    if (previousCursor) {
      console.log('Navigating to previous page with cursor:', previousCursor);
      
      /* Extract non-cursor params to maintain filters when paginating */
      let params = {};
      
      try {
        const urlObj = new URL(previousCursor);
        // Copy all parameters, including cursor which is needed for the API
        urlObj.searchParams.forEach((value, key) => {
          params[key] = value;
        });
        
        console.log('Extracted params for previous page:', params);
        // Pass empty string as search term to avoid overriding search params in the cursor
        fetchData(previousCursor, '', params);
      } catch (error) {
        console.error('Error parsing previousCursor URL:', error);
        // Fallback to simpler approach if URL parsing fails
        fetchData(previousCursor, searchTerm);
      }
    }
  }, [previousCursor, fetchData, searchTerm]);

  const searchCustomers = useCallback((term, paramsString = '', filterParams = {}) => {
    setSearchTerm(term);

    let params = {};

    // Merge paramsString (URLSearchParams) into params
    if (paramsString) {
      if (typeof paramsString === 'string') {
        const searchParams = new URLSearchParams(paramsString);
        searchParams.forEach((value, key) => {
          params[key] = value;
        });
      }
    }

    // Merge filterParams (object) into params (overrides any duplicate keys)
    if (filterParams && typeof filterParams === 'object') {
      params = { ...params, ...filterParams };
    }

    // Always request sync_statuses
    params.include_quickbooks_sync_info = true;

    if (!term?.trim() && Object.keys(params).length === 0) {
      fetchData(null);
      return;
    }
    fetchData(null, term, params);
  }, [fetchData]);

  /* Initialize on mount with intentionally limited dependencies
     to prevent infinite render loops */
  useEffect(() => {
    fetchData(null, searchTerm);
  }, [fetchData, searchTerm]);
  
  const handleSyncCustomer = (customerId, syncType) => {
    const customerToSync = customers.find(c => c.id === customerId);
    if (!customerToSync) {
      toast({
        title: 'Error',
        description: 'Customer not found',
        status: 'error',
        duration: 10000,
        isClosable: true
      });
      return;
    }
    
    setSyncType('all');
    setFetchMode('cdc');
    setImportMode('import');
    
    // Set the dates to the GL range by default, but they'll only be used
    // if the user selects one of the sync options
    setSyncDates(GL_SYNC_DATES);
    
    setSelectedCustomerForSync(customerToSync);
    openSyncModal();
  };
  
  const performCustomerSync = async (syncParams) => {
    if (!selectedCustomerForSync) return;
    const customerId = selectedCustomerForSync.id;
    setSyncingCustomerId(customerId);
    closeSyncModal();
    
    // Create a more descriptive message based on what's being synced
    let syncDescription = '';
    if (syncParams.syncType && syncParams.fetchMode) {
      // New simple sync
      syncDescription = `${syncParams.syncType} sync (${syncParams.fetchMode})`;
    } else {
      // Legacy sync
      const syncTypes = [];
      if (syncParams.transactions) syncTypes.push('transactions');
      if (syncParams.trialBalance) syncTypes.push('trial balance');
      syncDescription = syncTypes.join(' and ');
    }
    toast({ 
      title: 'Sync Initiated', 
      description: `Syncing ${syncDescription} for ${selectedCustomerForSync.name}...`, 
      status: 'info',
      duration: 10000,
      isClosable: true
    });
    try {
      console.log('performCustomerSync called with syncParams:', syncParams);
      let apiParams;
      
      if (syncParams.syncType && syncParams.fetchMode) {
        console.log('Using NEW sync path');
        // New simple sync parameters
        apiParams = {
          syncType: syncParams.syncType,
          fetchMode: syncParams.fetchMode,
          importMode: syncParams.importMode || 'import'
        };
        
        // Add date parameters only for range mode
        if (syncParams.fetchMode === 'range') {
          if (syncParams.syncType === 'transactions' || syncParams.syncType === 'all') {
            apiParams.gl_start_date = syncParams.gl_start_date || syncParams.glDates?.startDate;
            apiParams.gl_end_date = syncParams.gl_end_date || syncParams.glDates?.endDate;
          }
          if (syncParams.syncType === 'balances' || syncParams.syncType === 'all') {
            apiParams.tb_start_date = syncParams.tb_start_date || syncParams.tbDates?.startDate;
            apiParams.tb_end_date = syncParams.tb_end_date || syncParams.tbDates?.endDate;
          }
        }
      } else {
        console.log('Using LEGACY sync path');
        // Legacy sync - determine syncType based on what's selected
        const syncType = syncParams.transactions && syncParams.trialBalance ? "all" :
                        syncParams.transactions ? "transactions" : "balances";
        
        apiParams = {
          syncType,
          fetchMode: "range",
          importMode: "import"
        };
        
        // Only add dates for the sync types that are actually selected
        if (syncParams.transactions) {
          apiParams.gl_start_date = syncParams.glDates.startDate;
          apiParams.gl_end_date = syncParams.glDates.endDate;
        }
        if (syncParams.trialBalance) {
          apiParams.tb_start_date = syncParams.tbDates.startDate;
          apiParams.tb_end_date = syncParams.tbDates.endDate;
        }
      }
      console.log('Final apiParams before sending to API:', apiParams);
      const response = await quickbooksService.syncQuickbooksData({ 
        customerId: customerId, 
        ...apiParams 
      });
      
      // Check if this was a synchronous CDC sync that completed immediately
      if (response.data.completed_sync) {
        setSyncingCustomerId(null);
        toast({ 
          title: 'Sync Completed', 
          description: `Successfully completed ${syncDescription} for ${selectedCustomerForSync.name}.`, 
          status: 'success',
          duration: 10000,
          isClosable: true
        });
        return; // Don't navigate or poll
      }
      
      // If the response contains a job_id, start polling for completion
      if (response.data && response.data.job_id) {
        const jobId = response.data.job_id;
        
        // Start polling for job completion
        const pollJobStatus = async () => {
          try {
            const jobResponse = await quickbooksService.getJobDetails(jobId);
            const jobData = jobResponse.data;
            
            if (jobData.status === 'completed' || jobData.status === 'failed') {
              setSyncingCustomerId(null);
              
              if (jobData.status === 'completed') {
                toast({ 
                  title: 'Sync Completed', 
                  description: `Successfully completed ${syncDescription} for ${selectedCustomerForSync.name}.`, 
                  status: 'success',
                  duration: 10000,
                  isClosable: true
                });
              } else {
                toast({ 
                  title: 'Sync Failed', 
                  description: `Failed to sync ${syncDescription} for ${selectedCustomerForSync.name}. ${jobData.error || ''}`, 
                  status: 'error',
                  duration: 10000,
                  isClosable: true
                });
              }
              return true; // Stop polling
            }
            return false; // Continue polling
          } catch (error) {
            console.error('Error polling job status:', error);
            if (error.response && error.response.status === 404) {
              setSyncingCustomerId(null);
              return true; // Stop polling
            }
            return false; // Continue polling
          }
        };
        
        // Start polling
        const poll = async () => {
          const shouldStop = await pollJobStatus();
          if (!shouldStop) {
            setTimeout(poll, 3000); // Poll every 3 seconds
          }
        };
        setTimeout(poll, 2000); // Start after 2 seconds
      } else {
        console.log("No job id returned.")
        setSyncingCustomerId(null); // Reset if no job ID
      }
      // Set a flag in sessionStorage to indicate we've just started a sync
      // This helps prevent false failure notifications
      sessionStorage.setItem(`sync_started_${customerId}`, new Date().toISOString());
      
      /* Job status updates arrive via WebSocket rather than in the API response */
    } catch (error) {
      console.error('Error starting sync:', error);
      toast({ 
        title: 'Sync Failed', 
        description: error.response?.data?.error || 'Failed to start sync.', 
        status: 'error',
        duration: 10000,
        isClosable: true
      });
      setSyncingCustomerId(null);
    }
  };

  const updateCustomerInList = useCallback((updatedCustomer) => {
    /* Update both customer lists to maintain consistency */
    setCustomers(prevCustomers =>
      prevCustomers.map(customer =>
        customer.id === updatedCustomer.id 
          ? { ...customer, ...updatedCustomer } 
          : customer
      )
    );
    setFilteredCustomers(prevCustomers =>
      prevCustomers.map(customer =>
        customer.id === updatedCustomer.id 
          ? { ...customer, ...updatedCustomer } 
          : customer
      )
    );
    
    /* Clear loading state when the update is for the syncing customer */
    if (syncingCustomerId === updatedCustomer.id) {
      setSyncingCustomerId(null);
    }
    if (fullSyncingCustomerId === updatedCustomer.id) {
      setFullSyncingCustomerId(null);
    }
  }, [syncingCustomerId, fullSyncingCustomerId]);

  const handleJobUpdate = useCallback((jobPayload, action) => {
    setActiveJobs(prevJobs => {
      if (action === 'start') {
        if (!prevJobs.some(job => job.id === jobPayload.id)) {
          return [...prevJobs, jobPayload];
        }
      } else if (action === 'finish') {
        return prevJobs.filter(job => job.id !== jobPayload.id);
      }
      return prevJobs;
    });
  }, []);

  const handleConnectQuickbooks = async (customerId) => {
    try {
      /* Store in sessionStorage for OAuth flow which requires page reload */
      sessionStorage.setItem("customer_id", customerId);
      sessionStorage.setItem("qb_connect_origin", "accountmanagement");
      sessionStorage.setItem("qb_connect_page_params", window.location.search || "");
      
      const response = await quickbooksService.getAuthUrl(customerId);
      
      if (response.data && response.data.auth_url) {
        window.location.href = response.data.auth_url;
      } else {
        toast({
          title: 'Error',
          description: 'Failed to get QuickBooks authorization URL',
          status: 'error',
          duration: 10000,
          isClosable: true
        });
      }
    } catch (error) {
      console.error('Error getting QuickBooks auth URL:', error);
      toast({
        title: 'Error',
        description: 'Failed to connect to QuickBooks',
        status: 'error',
        duration: 10000,
        isClosable: true
      });
    }
  };

  const handleDisconnectQuickbooks = async (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (!customer) {
      toast({
        title: 'Error',
        description: 'Customer not found',
        status: 'error',
        duration: 10000,
        isClosable: true
      });
      return;
    }
    
    setSelectedCustomerForDisconnect(customer);
    openDisconnectModal();
  };

  const confirmDisconnectQuickbooks = async () => {
    if (!selectedCustomerForDisconnect) return;
    
    closeDisconnectModal();
    
    try {
      await quickbooksService.disconnectQuickbooks(selectedCustomerForDisconnect.id);
      toast({
        title: 'Disconnected',
        description: `QuickBooks connection has been removed for ${selectedCustomerForDisconnect.name}.`,
        status: 'success',
        duration: 4000,
        isClosable: true
      });
      // Optionally, refresh data
      fetchData();
    } catch (error) {
      console.error('Error disconnecting QuickBooks:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to disconnect QuickBooks.',
        status: 'error',
        duration: 10000,
        isClosable: true
      });
    } finally {
      setSelectedCustomerForDisconnect(null);
    }
  };

  const handleFullSyncCustomer = async (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (!customer) {
      toast({
        title: 'Error',
        description: 'Customer not found',
        status: 'error',
        duration: 10000,
        isClosable: true
      });
      return;
    }

    setFullSyncingCustomerId(customerId);

    try {
      const response = await quickbooksService.syncQuickbooksData({
        customerId,
        syncType: 'all',
        fetchMode: 'full',
        importMode: 'raw_only',
      });
      
      // Handle async response (HTTP 202)
      if (response.status === 202 && response.data.task_id) {
        toast({
          title: 'Fetch Task Started',
          description: `Redirecting to job details page to monitor progress...`,
          status: 'success',
          duration: 3000,
          isClosable: true
        });
        
        // Reset the loading state immediately
        setFullSyncingCustomerId(null);
        
        // Redirect to the sync job details page to view this workflow using React Router
        navigate(`/sync-jobs/${response.data.task_id}`);
        return;
      } else {
        // Fallback for immediate response format (if any endpoints still use it)
        toast({
          title: 'Fetch Completed',
          description: `Successfully fetched transaction data for ${customer.name}.`,
          status: 'success',
          duration: 10000,
          isClosable: true
        });
        setFullSyncingCustomerId(null);
      }
    } catch (error) {
      console.error('Error starting fetch task:', error);
      
      // Handle different error response formats
      let errorMessage = 'Failed to start fetch task.';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.errors) {
        const firstError = error.response.data.errors[0];
        if (typeof firstError === 'string') {
          errorMessage = firstError;
        } else if (typeof firstError === 'object' && firstError.error) {
          errorMessage = firstError.error;
          if (firstError.detail && Array.isArray(firstError.detail)) {
            errorMessage += `: ${firstError.detail.join(', ')}`;
          }
        }
      }
      
      toast({ 
        title: 'Fetch Task Failed', 
        description: errorMessage, 
        status: 'error',
        duration: 15000,
        isClosable: true
      });
      
      setFullSyncingCustomerId(null);
    }
  };

  const saveCustomerNote = async (customerId, note) => {
    try {
      await updateCustomer(customerId, { note });
      // Directly update the note in local state
      updateCustomerInList({ id: customerId, note: typeof note === 'string' ? note : note?.note });
      toast({
        title: 'Note Saved',
        description: 'Customer note has been updated.',
        status: 'success',
        duration: 4000,
        isClosable: true
      });
      return true;
    } catch (error) {
      console.error('Error saving customer note:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to save note.',
        status: 'error',
        duration: 10000,
        isClosable: true
      });
      return false;
    }
  };

  return {
    customers: filteredCustomers,
    allCustomers: customers,
    customersMap,
    loading,
    nextCursor,
    previousCursor,
    syncingCustomerId,
    fullSyncingCustomerId,
    activeJobs,
    fetchData,
    handleNextPage,
    handlePreviousPage,
    handleSyncCustomer,
    handleFullSyncCustomer,
    updateCustomerInList,
    handleJobUpdate,
    searchCustomers,
    isSyncModalOpen,
    closeSyncModal,
    selectedCustomerForSync,
    syncType,
    setSyncType,
    fetchMode,
    setFetchMode,
    importMode,
    setImportMode,
    syncDates,
    setSyncDates,
    glDates,
    setGlDates,
    tbDates,
    setTbDates,
    performCustomerSync,
    handleConnectQuickbooks,
    handleDisconnectQuickbooks,
    isDisconnectModalOpen,
    closeDisconnectModal,
    selectedCustomerForDisconnect,
    confirmDisconnectQuickbooks,
    saveCustomerNote
  };
}; 
